import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { faqItems } from "@/data/site/faq";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

const categoryLabels: Record<string, string> = {
  general: "General",
  process: "How We Work",
  fees: "Fees & Engagement",
  outcomes: "Outcomes",
};

const FAQ = () => {
  const grouped = faqItems.reduce<Record<string, typeof faqItems>>((acc, item) => {
    (acc[item.category] ??= []).push(item);
    return acc;
  }, {});

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
      // speakable is emitted separately via PageSEO's speakableSelectors prop
      // (as a WebPage node) to match Google's recommended pattern.
    },
  ];

  return (
    <SiteLayout>
      <PageSEO
        title="Frequently Asked Questions"
        description="Common questions about tax resolution, the IRS, our process, and our fees — answered by Comprehensive Tax Resolution LLC."
        path="/faq"
        image={stockImages.accountingDesk.src}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "FAQ", href: "/faq" },
        ]}
        jsonLd={jsonLd}
        speakableSelectors={[".faq-question", ".faq-answer"]}
      />
      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.accountingDesk} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Reveal>
            <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "FAQ" }]} tone="dark" />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mt-2 mb-4">
              Honest answers
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
              Frequently asked questions.
            </h1>
            <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
              Plain-language answers about working with a tax-resolution CPA.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Category jump nav */}
          <nav aria-label="Jump to FAQ category" className="mb-12 flex flex-wrap gap-2">
            {Object.keys(grouped).map((cat) => (
              <a
                key={cat}
                href={`#faq-${cat}`}
                className="px-4 py-2 rounded border border-border text-sm font-semibold text-navy hover:bg-gold hover:border-gold hover:text-navy transition-colors"
              >
                {categoryLabels[cat] ?? cat}
              </a>
            ))}
          </nav>

          <div className="space-y-16">
            {Object.entries(grouped).map(([cat, items]) => (
              <Reveal key={cat}>
                <section id={`faq-${cat}`} className="scroll-mt-24">
                  <h2 className="font-heading text-2xl font-bold text-navy mb-6 leading-tight border-b-2 border-gold pb-3 inline-block">
                    {categoryLabels[cat] ?? cat}
                  </h2>
                  <RevealStagger className="divide-y divide-border border-y border-border" stagger={50}>
                    {items.map((f) => (
                      <RevealItem key={f.q}>
                        <details className="group py-5">
                          <summary className="faq-question font-heading font-semibold text-navy cursor-pointer list-none flex justify-between items-center gap-4 text-lg">
                            {f.q}
                            <span className="text-gold group-open:rotate-45 transition-transform text-2xl leading-none flex-shrink-0">
                              +
                            </span>
                          </summary>
                          <p className="faq-answer text-foreground/80 mt-4 leading-relaxed">{f.a}</p>
                        </details>
                      </RevealItem>
                    ))}
                  </RevealStagger>
                </section>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default FAQ;
